import type { ICheckoutContext } from '@/types/context';
import type {
  ChargebeeAuthorizeAdditionalData,
  ChargebeeBillingAddress,
  ChargebeeCardComponent,
  ChargebeePaymentIntent,
} from '@/types/chargebee';
import {
  isChargebeePaymentIntentAuthorized,
  isChargebeePaymentIntentAuthorizedError,
  isHydratedChargebeePaymentIntent,
} from '@/utils/chargebeePaymentIntent';
import { chargebeeService, type ChargebeeMountedPaymentComponent } from '@/services/chargebeeService';

type SendPaymentToServer = (params: unknown) => Promise<unknown>;

/**
 * What `ChargebeeGenericPayForm` hands `runPayment`: the mounted Payment
 * Component for a Pay-button attempt (like StripeGenericPayForm's `elements`),
 * or the intent Chargebee already authorized on a button it rendered itself
 * (the wallets' shape).
 */
export interface ChargebeeGenericPaymentParams {
  paymentComponent?: Pick<ChargebeeMountedPaymentComponent, 'authorize'>;
  paymentIntent?: ChargebeePaymentIntent;
}

/**
 * Chargebee (subscriptions) card payment. Chargebee owns the ENTIRE 3DS flow
 * inside `authorizeWith3ds` — challenge included — and resolves the intent in
 * `authorized` state, so v3's `threeDSSecurityState` / ForterCard3DSForm
 * machinery is deliberately not involved. `sendPaymentToServer` arrives from
 * SubscriptionCheckoutContent as `sendSubscriptionToServer`
 * (POST /checkout/subscriptions/finalize-subscription).
 */
export const handleChargebeeCardPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: SendPaymentToServer
): Promise<unknown> => {
  const { subscription } = checkout;
  if (!subscription) {
    throw new Error('Chargebee card payment requires the subscription checkout context');
  }
  const cardComponent = checkout.card.cardElementRef?.current as ChargebeeCardComponent | null;
  if (!cardComponent?.authorizeWith3ds) {
    throw new Error('Chargebee card component is not mounted');
  }

  subscription.setPaymentIntent(await authorizePaymentIntent(cardComponent, checkout, subscription));

  return await sendPaymentToServer({});
};

/**
 * Authorization is a no-op when the intent is already authorized: it is
 * single-use, so re-authorizing earns a `payment_intent_authorized` rejection
 * and strands the shopper on a session that can NEVER be paid (the intent id
 * is fixed for the session, and only finalize consumes it). Both the
 * up-front check and the catch below therefore fall through to finalize.
 */
const authorizePaymentIntent = async (
  cardComponent: ChargebeeCardComponent,
  checkout: ICheckoutContext,
  subscription: NonNullable<ICheckoutContext['subscription']>
): Promise<ChargebeePaymentIntent> => {
  const paymentIntent = await resolvePaymentIntent(subscription);
  if (isChargebeePaymentIntentAuthorized(paymentIntent)) return paymentIntent;

  try {
    return await chargebeeService.authorizeCardWith3ds(
      cardComponent,
      paymentIntent,
      buildChargebeeAdditionalData(checkout)
    );
  } catch (error) {
    // Raced with an earlier attempt (double click, or a retry after the
    // cached copy went stale) — re-read the authorized intent and finalize.
    if (!isChargebeePaymentIntentAuthorizedError(error)) throw error;
    return await refreshPaymentIntent(subscription, paymentIntent);
  }
};

/**
 * Wallet finalize step, shared by the Google Pay / Apple Pay dispatch cases.
 * The wallet forms already ran Chargebee's `handlePayment()` (the sheet is
 * tied to Chargebee's mounted button), so all that's left is to store the
 * authorized intent and finalize the subscription server-side.
 */
export const finalizeChargebeeWalletPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: SendPaymentToServer,
  authorizedIntent?: ChargebeePaymentIntent
): Promise<unknown> => {
  if (!checkout.subscription) {
    throw new Error('Chargebee wallet payment requires the subscription checkout context');
  }
  if (authorizedIntent) {
    checkout.subscription.setPaymentIntent(authorizedIntent);
  }
  return await sendPaymentToServer({});
};

/**
 * Long-tail APMs (Kakao Pay, Naver Pay, KR card, …) through Chargebee's Payment
 * Component — the `dispatchByProvider` CHARGEBEE case. Chargebee owns
 * validation, the redirect / bank window and any authentication inside
 * `authorize()`, and resolves the intent in `authorized` state; the rest is the
 * same store-then-finalize the card and wallet handlers do.
 */
export const handleChargebeeGenericPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: SendPaymentToServer,
  params?: ChargebeeGenericPaymentParams
): Promise<unknown> => {
  const { subscription } = checkout;
  if (!subscription) {
    throw new Error('Chargebee payment requires the subscription checkout context');
  }

  subscription.setPaymentIntent(await authorizeGenericPaymentIntent(subscription, params));

  return await sendPaymentToServer({});
};

/**
 * Same single-use-intent rule as the card path: an intent an earlier attempt
 * already authorized (Chargebee's own button, or a finalize that failed) is
 * finalized as-is rather than re-confirmed.
 */
const authorizeGenericPaymentIntent = async (
  subscription: NonNullable<ICheckoutContext['subscription']>,
  params?: ChargebeeGenericPaymentParams
): Promise<ChargebeePaymentIntent> => {
  if (params?.paymentIntent) return params.paymentIntent;
  if (isChargebeePaymentIntentAuthorized(subscription.paymentIntent)) return subscription.paymentIntent;
  if (!params?.paymentComponent) {
    throw new Error('Chargebee payment component is not mounted');
  }
  try {
    return await params.paymentComponent.authorize();
  } catch (error) {
    // Chargebee authorized it on an attempt whose result never reached our
    // state (confirm rejecting after `onSuccess`, a racing second confirm) —
    // re-read and finalize, as the card path does.
    if (!isChargebeePaymentIntentAuthorizedError(error) || !subscription.paymentIntent) throw error;
    return await refreshPaymentIntent(subscription, subscription.paymentIntent);
  }
};

/**
 * `useChargebeePaymentIntent` normally has the full intent in context by the
 * time the shopper can submit. This covers the tail: a retrieval that failed
 * or hasn't landed yet would otherwise reach Chargebee as `{ id }` and be
 * rejected with INVALID_PAYMENT_INTENT before any PSP call.
 */
const resolvePaymentIntent = async (
  subscription: NonNullable<ICheckoutContext['subscription']>
): Promise<ChargebeePaymentIntent> => {
  if (isHydratedChargebeePaymentIntent(subscription.paymentIntent)) {
    return subscription.paymentIntent;
  }
  if (!subscription.cbInstance) {
    throw new Error('Chargebee SDK is not ready');
  }
  const paymentIntent = await chargebeeService.retrievePaymentIntent(subscription.paymentIntent.id);
  subscription.setPaymentIntent(paymentIntent);
  return paymentIntent;
};

/** Re-reads the server-side intent; the local copy is stale by definition here. */
const refreshPaymentIntent = async (
  subscription: NonNullable<ICheckoutContext['subscription']>,
  fallback: ChargebeePaymentIntent
): Promise<ChargebeePaymentIntent> => {
  if (!subscription.cbInstance) return fallback;
  try {
    return await chargebeeService.retrievePaymentIntent(fallback.id);
  } catch {
    return fallback;
  }
};

/** Country rides along to improve frictionless-3DS odds. */
const buildChargebeeAdditionalData = (checkout: ICheckoutContext): ChargebeeAuthorizeAdditionalData => {
  const billingAddress = buildBillingAddress(checkout);
  return {
    plan: checkout.subscription?.plan?.itemPriceId,
    email: checkout.customer?.email,
    ...(billingAddress ? { billingAddress } : {}),
  };
};

const buildBillingAddress = (checkout: ICheckoutContext): ChargebeeBillingAddress | undefined => {
  const countryCode = checkout.customer?.taxCountry ?? checkout.customer?.geoLocator?.countryCode2;
  if (!countryCode) return undefined;
  return { countryCode };
};
